import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useCallback, useEffect, useState } from "react";
import { CrossIcon } from "../../assets/icons";
import {
  AdminPlanResponse,
  CreatePlanRequest,
  DeliveryFeeRequest,
  DeliveryFeeResponse,
  FileResponse,
  UploadImageImageTypeEnum,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { cdnUrl, checkIf, findImageByType, maxFileSize, slugify } from "../../utils";
import Transition from "../Transition";
import useValidation from "../Validation";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import { Toggle } from "../Switch";
import { DropzoneArea } from "mui-file-dropzone";
import { usePlans } from "../../hooks/usePlans";
import useZones from "../../hooks/useZones";
import useAreasBrief from "../../hooks/useAreaBrief";

interface IDeliveryFeeCrud extends IDialogProps {
  deliveryFee?: DeliveryFeeResponse;
}
interface UpdateDeliveryFeeRequest extends DeliveryFeeRequest {
  id?: string;
}

interface IPlan {
  id?: string;
  name?: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 516,
    },
  },
};

function getStyles(id: string, plan: readonly string[]) {
  return {
    fontWeight: plan.includes(id) ? 700 : 400,
  };
}

export default function Fee(props: IDeliveryFeeCrud) {
  const planList = usePlans();
  const zones = useZones();
  const areas = useAreasBrief();
  const [formDirty, setFormDirty] = useState(false);
  const [deliveryFee, setFeeEdit] = useState<any>({
    area: null,
    zone: null,
    fee: "",
    planIds: [],
  });
  const dispatch = useDispatch();
  const { validation, setValidation } = useValidation(deliveryFee);
  const [loading, setLoading] = useState(false);
  const [planListSelected, setPlanListSelected] = useState<string[]>([]);

  const handleFieldChange = (field: any, value?: any) => {
    setFormDirty(true);
    if (typeof field === "string") {
      setFeeEdit((state: any) => ({ ...state, [field]: value }));
    } else {
      setFeeEdit((state: any) => ({ ...state, ...field }));
    }
  };

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    let FeeReqDto: UpdateDeliveryFeeRequest = {
      ...deliveryFee,
      area: deliveryFee.area?.name,
    };

    try {
      setLoading(true);
      let message;

      if (props.create) {
        message = "Delivery fee has been created successfully";
        await Api.DeliveryFee.createDeliveryFee(FeeReqDto);
        resetForm();
        props.onConfirm(true);
        props.onClose({});
      } else if (props.edit) {
        message = "Delivery fee has been updated successfully";
        const { id, ...UpdateFeeReqDto } = FeeReqDto;
        await Api.DeliveryFee.updateDeliveryFee(String(id), UpdateFeeReqDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFeeEdit({
      area: null,
      zone: null,
      fee: "",
      planIds: [],
    });
    setPlanListSelected([]);
    setValidation((state) => ({ ...state, show: false }));
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  const getName = useCallback(
    (id: string) => {
      return planList.find((i) => i.id === id)?.name;
    },
    [planList]
  );

  useEffect(() => {
    const getDeliveryFeeById = async (feeId: string) => {
      try {
        const res = await Api.DeliveryFee.getDeliveryFee(feeId);
        const fee = res.data;
        setFeeEdit(fee);
        setPlanListSelected(fee.plans?.map((i) => i.id) as string[]);
      } catch (error) {}
    };
    if (props.deliveryFee) {
      getDeliveryFeeById(String(props.deliveryFee.id));
    }
  }, [props.deliveryFee]);

  const handlePlanListChange = (event: SelectChangeEvent<typeof planListSelected>) => {
    setPlanListSelected(event.target.value as string[]);
  };

  useEffect(() => {
    setFeeEdit((state: any) => ({
      ...state,
      area: typeof state.area === "string" ? areas.find((i) => i.name === state.area) : state.area,
      planIds: planListSelected.map((i) => i),
    }));
  }, [planListSelected, areas]);

  // console.log("planById", plan);
  // console.log("planListSelected", planListSelected);
  // console.log("fee", deliveryFee);
  // console.log("portions", portions);
  // console.log("validation", validation);
  // console.log("areas data:", areas);
  // console.log("areas with duplicate names:", areas.filter((area, index, arr) =>
  //   arr.findIndex(a => a.name === area.name) !== index
  // ));
  // console.log("areas with duplicate ids:", areas.filter((area, index, arr) =>
  //   arr.findIndex(a => a.id === area.id) !== index
  // ));

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "564px",
            maxWidth: "564px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <FormControl sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
              <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Zone
                    </Typography>
                  </Box>

                  <Autocomplete
                    className="form-field"
                    value={deliveryFee.zone || null}
                    options={zones}
                    getOptionLabel={(zone) => zone.toString()}
                    onChange={(event: any, newValue: number | null) => {
                      handleFieldChange({ zone: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={validation.show && validation.zone?.error}
                        helperText={validation.show && validation.zone?.error ? validation.zone.helperText : undefined}
                      />
                    )}
                  />
                </Box>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Area
                    </Typography>
                  </Box>

                  <Autocomplete
                    className="form-field"
                    value={deliveryFee.area || null}
                    options={areas}
                    getOptionLabel={(area) => area.name?.toString() || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderOption={(props, option, { index }) => {
                      const uniqueKey = `area-${option.id}-${index}`;
                      return (
                        <li {...props} key={uniqueKey} data-option-id={option.id}>
                          {option.name}
                        </li>
                      );
                    }}
                    onChange={(event: any, newValue: string | null) => {
                      handleFieldChange({ area: newValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={validation.show && validation.area?.error}
                        helperText={validation.show && validation.area?.error ? validation.area.helperText : undefined}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Plan selection
                  </Typography>
                </Box>

                <FormControl sx={{ m: 0, width: "100%" }}>
                  <Select
                    multiple
                    value={planListSelected}
                    onChange={handlePlanListChange}
                    input={<OutlinedInput sx={{ height: "auto", marginBottom: 0 }} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={getName(value)} />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {planList.map((i) => (
                      <MenuItem key={i.id} value={i.id} style={getStyles(String(i.id), planListSelected)}>
                        {i.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", columnGap: "12px", alignItems: "baseline" }}>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Fee
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <TextField
                    className="form-field"
                    value={deliveryFee?.fee}
                    error={validation.show && validation.fee?.error}
                    helperText={validation.show && validation.fee?.error ? validation.fee.helperText : undefined}
                    onChange={(e) => handleFieldChange({ fee: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ color: "text.fontLight3", fontSize: "14px", fontWeight: 500 }}>
                            QAR
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
